import React, { useState, useEffect } from 'react';
import { CheckCircle, MessageCircle, Brain, Smartphone, Clock, TrendingUp, Phone } from 'lucide-react';
import RegistrationModal from './RegistrationModal';
import ReferralRegistrationModal from './ReferralRegistrationModal';
import LoginModal from './LoginModal';
import PasswordResetModal from './PasswordResetModal';
import UnifiedOnboardingFlow from './onboarding/UnifiedOnboardingFlow';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../utils/analytics';
import { formatPhoneNumber } from '../utils/validation';
import { useToast } from '../hooks/useToast';
import AvatarSelector from './ui/AvatarSelector';

const countryCodes = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
];

export default function LandingPage() {
  const { user } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false);
  const [isReferralRegistrationOpen, setIsReferralRegistrationOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [isUnifiedOnboardingOpen, setIsUnifiedOnboardingOpen] = useState(false);
  // const [selectedPersonality, setSelectedPersonality] = useState<string | null>(null);
  const { success } = useToast();

  useEffect(() => {
    analytics.page('Landing Page');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[id]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    analytics.track('phone_number_submitted', {
      country_code: countryCode,
      phone_length: phoneNumber.replace(/\D/g, '').length
    });
    setIsUnifiedOnboardingOpen(true);
  };

  const handleSignUpClick = () => {
    analytics.track('signup_button_clicked', { location: 'header' });
    setIsUnifiedOnboardingOpen(true);
  };

  const handleLoginClick = () => {
    analytics.track('login_button_clicked', { location: 'header' });
    setIsLoginOpen(true);
  };

  // const handleReferralClick = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   analytics.track('referral_link_clicked');
  //   setIsReferralRegistrationOpen(true);
  // };

  const handleRegistrationComplete = () => {
    setIsRegistrationOpen(false);
  };

  const handleReferralRegistrationComplete = () => {
    setIsReferralRegistrationOpen(false);
  };

  const handleUnifiedOnboardingComplete = () => {
    setIsUnifiedOnboardingOpen(false);

    if (user) {
      success('Welcome to Priorityone!', 'Your AI coach is ready to help you achieve your goals!');
    } else {
      success('Account Created!', 'Please check your email to verify your account before logging in.');
    }
  };

  const handleForgotPassword = () => {
    setIsLoginOpen(false);
    setIsPasswordResetOpen(true);
  };

  const handleBackToLogin = () => {
    setIsPasswordResetOpen(false);
    setIsLoginOpen(true);
  };

  return (
    <div className="min-h-screen bg-white font-inter">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Priorityone Today</h1>
                <p className="text-xs text-gray-500 -mt-1">by Arad & Baker Management Services</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="#how-it-works"
                className="text-gray-600 hover:text-gray-900 transition-colors flex items-center focus:outline-none focus:underline"
                onClick={() => analytics.track('navigation_clicked', { section: 'how-it-works' })}
              >
                How It Works
              </a>
              <button
                onClick={handleLoginClick}
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium focus:outline-none focus:underline"
              >
                Login
              </button>
              <button
                onClick={handleSignUpClick}
                className="bg-emerald-500 text-white px-6 py-2 rounded-full hover:bg-emerald-600 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Sign Up
              </button>
            </nav>
            <div className="md:hidden flex items-center space-x-3">
              <button
                onClick={handleLoginClick}
                className="text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm focus:outline-none focus:underline"
              >
                Login
              </button>
              <button
                onClick={handleSignUpClick}
                className="bg-emerald-500 text-white px-4 py-2 rounded-full hover:bg-emerald-600 transition-colors font-medium text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Sign Up
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-24 pb-20 bg-gradient-to-br from-pink-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transform transition-all duration-1000 ${isVisible.home ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Accomplish<br />
                <span className="text-pink-400">Your Priority</span> Task Every Day
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                WhatsApp-based AI accountability that cuts through the noise. Get your most important task done, every single day.
              </p>

              <form onSubmit={handlePhoneSubmit} className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Start Your Daily Wins</h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 flex space-x-2">
                    <label htmlFor="country-code" className="sr-only">Country code</label>
                    <select
                      id="country-code"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <div className="flex-1 relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <label htmlFor="phone-number" className="sr-only">Phone number</label>
                      <input
                        id="phone-number"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                        placeholder="123-456-7890"
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        required
                        aria-describedby="phone-help"
                      />
                      <p id="phone-help" className="sr-only">
                        Enter your phone number to receive WhatsApp messages
                      </p>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="bg-emerald-500 text-white px-8 py-3 rounded-xl hover:bg-emerald-600 transition-colors font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                  >
                    Begin
                  </button>
                </div>
              </form>
            </div>

            <div className={`transform transition-all duration-1000 delay-300 ${isVisible.home ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="relative">
                <img
                  src="/hero_image.jpg"
                  alt="Professional manager in modern office looking at phone with contentment"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pink-400/20 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['how-it-works'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, effective, and designed to fit seamlessly into your daily routine
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <MessageCircle className="h-12 w-12 text-pink-400" />,
                title: "Morning Prompt",
                description: "We text you: \"What's your Priorityone task for today?\""
              },
              {
                icon: <CheckCircle className="h-12 w-12 text-pink-400" />,
                title: "Evening Check-in",
                description: "We follow up: \"Did you complete your priority task?\""
              },
              {
                icon: <Brain className="h-12 w-12 text-pink-400" />,
                title: "Smart Coaching",
                description: "AI learns your patterns and provides personalized guidance and reporting"
              }
            ].map((step, index) => (
              <div
                key={index}
                className={`bg-white p-8 rounded-2xl shadow-lg border border-gray-100 text-center transform transition-all duration-1000 hover:shadow-xl hover:-translate-y-2 focus-within:shadow-xl focus-within:-translate-y-2 ${
                  isVisible['how-it-works'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
                tabIndex={0}
              >
                <div className="mb-6 flex justify-center" aria-hidden="true">{step.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="coach-personality" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['coach-personality'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Coach Personality</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-16">
              Click on an avatar to hear a preview
            </p>
          </div>
          <AvatarSelector
            selectedPersonality={null}
            onSelect={() => {}}
          />
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible.benefits ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Priorityone Today Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built for busy people who need accountability without complexity
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Smartphone className="h-10 w-10 text-emerald-500" />,
                title: "No App Download",
                description: "Works with any phone via your WhatsApp"
              },
              {
                icon: <Brain className="h-10 w-10 text-emerald-500" />,
                title: "AI-Powered",
                description: "Learns your patterns and adapts over time"
              },
              {
                icon: <MessageCircle className="h-10 w-10 text-emerald-500" />,
                title: "Voice & Text",
                description: "Respond however feels natural"
              },
              {
                icon: <Clock className="h-10 w-10 text-emerald-500" />,
                title: "30-Second Setup",
                description: "Just your phone number and preferences"
              }
            ].map((benefit, index) => (
              <div
                key={index}
                className={`text-center transform transition-all duration-1000 ${
                  isVisible.benefits ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-gray-50 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 hover:bg-emerald-50 transition-colors" aria-hidden="true">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible.pricing ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600">
              One plan, everything included. No hidden fees.
            </p>
          </div>

          <div className={`bg-white rounded-3xl shadow-2xl border border-gray-100 p-8 md:p-12 transform transition-all duration-1000 ${isVisible.pricing ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <span className="text-5xl font-bold text-gray-900">$3</span>
                <span className="text-xl text-gray-600 ml-2">/month</span>
              </div>
              <p className="text-lg text-gray-600 mb-8">
                Unlimited messaging, voice responses, progress reports
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "Daily prompts via WhatsApp",
                  "Evening accountability check-ins",
                  "AI-powered progress tracking",
                  "Voice notes support",
                  "Weekly performance reports",
                ].map((feature, index) => (
                  <div key={index} className="flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-emerald-500 mr-3" aria-hidden="true" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  analytics.track('pricing_signup_clicked');
                  handleSignUpClick();
                }}
                className="bg-emerald-500 text-white px-8 py-4 rounded-xl hover:bg-emerald-600 transition-colors font-medium text-lg mb-6 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Start Your Daily Wins
              </button>

              {/* <div className="border-t border-gray-200 pt-6">
                <p className="text-sm text-gray-500 mb-2">
                  <TrendingUp className="h-4 w-4 inline mr-2" aria-hidden="true" />
                  Get 3 weeks free by{' '}
                  <button
                    onClick={handleReferralClick}
                    className="text-emerald-600 hover:text-emerald-700 underline font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 rounded"
                  >
                    sharing with 3 friends
                  </button>
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">Priorityone Today</h3>
              <p className="text-gray-400 mb-6 max-w-md">
                AI-powered accountability through WhatsApp. Accomplish your most important task, every single day.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a
                    href="./compliance/Priorityone Today - Privacy Policy.pdf"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'privacy' })}
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="./compliance/Priorityone Today - Terms of Service.pdf"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'terms' })}
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="./compliance/Priorityone Today - Refund and Dispute Policy.pdf"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'terms' })}
                  >
                    Refund and Dispute Policy
                  </a>
                </li>
                <li>
                  <a
                    href="./compliance/Priorityone Today - Cancellation Policy.pdf"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'terms' })}
                  >
                    Cancellation Policy
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:help@priorityone.today"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'contact' })}
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a
                    href="#"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'help' })}
                  >
                    Help Center
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'whatsapp-support' })}
                  >
                    WhatsApp Support
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-white transition-colors focus:outline-none focus:underline"
                    onClick={() => analytics.track('footer_link_clicked', { link: 'faq' })}
                  >
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex items-center justify-between">
  
            <p className="text-gray-400">
              © 2025 Priorityone Today by Arad & Baker Management Services All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isRegistrationOpen}
        onClose={() => setIsRegistrationOpen(false)}
        onComplete={handleRegistrationComplete}
      />

      {/* Referral Registration Modal */}
      <ReferralRegistrationModal
        isOpen={isReferralRegistrationOpen}
        onClose={() => setIsReferralRegistrationOpen(false)}
        onComplete={handleReferralRegistrationComplete}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
        onForgotPassword={handleForgotPassword}
      />

      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={isPasswordResetOpen}
        onClose={() => setIsPasswordResetOpen(false)}
        onBackToLogin={handleBackToLogin}
      />

      {/* Unified Onboarding Flow */}
      {isUnifiedOnboardingOpen && (
        <UnifiedOnboardingFlow
          onComplete={handleUnifiedOnboardingComplete}
          onClose={() => setIsUnifiedOnboardingOpen(false)}
          initialData={{ phoneNumber }}
        />
      )}
    </div>
  );
}
